import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface MemberChecklistItem {
  id: number;
  name: string;
}

const emptyForm = { name: '' };

export function MemberChecklistItems() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<MemberChecklistItem[] | null>(null);
  const [editing, setEditing] = useState<'new' | MemberChecklistItem | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<MemberChecklistItem[]>('/member-checklist-items').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: MemberChecklistItem) => {
    setForm({ name: item.name });
    setErrors({});
    setEditing(item);
  };

  const set = (e: ChangeEvent<HTMLInputElement>) => setForm({ name: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/member-checklist-items', form);
      else if (editing) await api.put(`/member-checklist-items/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: MemberChecklistItem) => {
    if (!(await confirmDelete(`Delete the checklist item "${item.name}"?`))) return;
    try {
      await api.delete(`/member-checklist-items/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Member Checklist Items</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={2} className="muted center">
                    No checklist items configured yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add checklist item' : 'Edit checklist item'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <input value={form.name} onChange={set} required placeholder="e.g. ID Submitted" />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
